/**
 * Contextual Tooltips System
 * Provides helpful hover tooltips for DAW controls with tips and shortcuts
 */
import { useState, useEffect, useRef, createContext, useContext, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Info, Keyboard, Lightbulb } from 'lucide-react';

// Tooltip content database
const TOOLTIP_CONTENT = {
  // Transport Controls
  'transport-play': {
    title: 'Play/Pause',
    description: 'Start or pause playback from the current position',
    shortcut: 'Space',
    tip: 'Double-click to restart from the beginning',
  },
  'transport-stop': {
    title: 'Stop',
    description: 'Stop playback and return to the start position',
    shortcut: 'Enter',
  },
  'transport-record': {
    title: 'Record',
    description: 'Start recording on the selected track',
    shortcut: 'R',
    tip: 'Make sure your microphone is connected and track is armed',
  },
  'transport-loop': {
    title: 'Loop',
    description: 'Toggle loop playback between markers',
    shortcut: 'L',
    tip: 'Set loop region by dragging on the timeline ruler',
  },
  
  // View Controls
  'view-edit': {
    title: 'Edit View',
    description: 'Timeline view for arranging and editing clips',
    shortcut: 'Ctrl+1',
  },
  'view-tab-mix': {
    title: 'Mix View',
    description: 'Mixer console for adjusting levels and panning',
    shortcut: 'Ctrl+2',
  },
  
  // Track Controls
  'add-audio-track': {
    title: 'Add Audio Track',
    description: 'Create a new audio track for recording or importing audio',
    tip: 'Drag audio files directly onto the timeline to auto-create tracks',
  },
  'add-midi-track': {
    title: 'Add MIDI Track',
    description: 'Create a track for virtual instruments (synths, drums)',
    tip: 'Pro/Studio subscription unlocks all MIDI instruments',
  },
  'duplicate-track': {
    title: 'Duplicate Track',
    description: 'Create an exact copy of this track with all settings',
    shortcut: 'Ctrl+D',
  },
  'delete-track': {
    title: 'Delete Track',
    description: 'Remove this track from the project',
    shortcut: 'Delete',
    tip: 'Use Ctrl+Z to undo if deleted by mistake',
  },
  'mute-track': {
    title: 'Mute',
    description: 'Silence this track during playback',
    shortcut: 'M',
  },
  'solo-track': {
    title: 'Solo',
    description: 'Play only this track (mute all others)',
    shortcut: 'S',
    tip: 'Click multiple Solo buttons to hear a group of tracks',
  },
  
  // Effects
  'toggle-effects': {
    title: 'Guitar Effects',
    description: 'Open the guitar amp and pedal effects chain',
    tip: 'Chain multiple effects for unique sounds',
  },
  'toggle-eq': {
    title: 'Equalizer',
    description: 'Shape the frequency balance of this track',
    tip: 'Cut frequencies before boosting for cleaner sound',
  },
  
  // Tools
  'tool-select': {
    title: 'Select Tool',
    description: 'Select and move clips, notes, or automation points',
    shortcut: 'V',
  },
  'tool-draw': {
    title: 'Draw Tool',
    description: 'Draw new clips, notes, or automation curves',
    shortcut: 'P',
  },
  'tool-erase': {
    title: 'Erase Tool',
    description: 'Delete clips, notes, or automation points',
    shortcut: 'E',
  },
  
  // Timeline
  'snap-grid': {
    title: 'Snap to Grid',
    description: 'Align edits to the selected grid resolution',
    tip: 'Hold Alt while dragging to temporarily disable snap',
  },
  'quantize': {
    title: 'Quantize',
    description: 'Align notes to the nearest grid position',
    shortcut: 'Q',
    tip: 'Adjust quantize strength for more natural feel',
  },
  
  // Metronome
  'metronome-toggle': {
    title: 'Metronome',
    description: 'Toggle click track during playback and recording',
    tip: 'Hover to adjust metronome volume',
  },
  
  // BPM
  'bpm-control': {
    title: 'Tempo (BPM)',
    description: 'Project tempo in beats per minute',
    tip: 'Click to tap tempo or scroll to fine-tune',
  },
  
  // Export
  'export-audio': {
    title: 'Export Audio',
    description: 'Render your project to an audio file',
    shortcut: 'Ctrl+Shift+E',
    tip: 'MP3 export requires Pro subscription',
  },
  
  // Save
  'save-project': {
    title: 'Save Project',
    description: 'Save your project to the cloud',
    shortcut: 'Ctrl+S',
  },
  
  // Keyboard Shortcuts
  'keyboard-shortcuts-btn': {
    title: 'Keyboard Shortcuts',
    description: 'View all available keyboard shortcuts',
    shortcut: '?',
  },
  
  // Tour
  'restart-tour-btn': {
    title: 'Quick Tour',
    description: 'Take an interactive tour of the DAW features',
  },
  
  // Piano Roll
  'open-piano-roll': {
    title: 'Piano Roll',
    description: 'Open the MIDI note editor for this clip',
    tip: 'Use velocity editing for dynamic expression',
  },
};

// Context for tooltip settings
const TooltipContext = createContext(null);

export const TooltipProvider = ({ children }) => {
  const [enabled, setEnabled] = useState(() => {
    const saved = localStorage.getItem('string-sauce-tooltips');
    return saved !== 'false'; // Default to enabled
  });
  
  const [delay, setDelay] = useState(500); // ms before showing tooltip

  useEffect(() => {
    localStorage.setItem('string-sauce-tooltips', enabled.toString());
  }, [enabled]);

  return (
    <TooltipContext.Provider value={{ enabled, setEnabled, delay, setDelay }}>
      {children}
    </TooltipContext.Provider>
  );
};

export const useTooltipSettings = () => {
  const context = useContext(TooltipContext);
  if (!context) {
    return { enabled: true, setEnabled: () => {}, delay: 500, setDelay: () => {} };
  }
  return context;
};

// Enhanced Tooltip Component
export const ContextualTooltip = ({ 
  id, 
  children, 
  content: customContent,
  position = 'top',
  className = '',
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const timeoutRef = useRef(null);
  const { enabled, delay } = useTooltipSettings();

  // Get content from database or use custom content
  const content = customContent || TOOLTIP_CONTENT[id];

  const showTooltip = useCallback(() => {
    if (!triggerRef.current || !content) return;
    
    const rect = triggerRef.current.getBoundingClientRect();
    const tooltipWidth = 280;
    const tooltipHeight = 120;
    const padding = 8;
    
    let top, left;
    
    switch (position) {
      case 'bottom':
        top = rect.bottom + padding;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        break;
      case 'left':
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        left = rect.left - tooltipWidth - padding;
        break;
      case 'right':
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        left = rect.right + padding;
        break;
      case 'top':
      default:
        top = rect.top - tooltipHeight - padding;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
    }
    
    // Clamp to viewport
    left = Math.max(padding, Math.min(left, window.innerWidth - tooltipWidth - padding));
    top = Math.max(padding, Math.min(top, window.innerHeight - tooltipHeight - padding));
    
    setTooltipPosition({ top, left });
    setIsVisible(true);
  }, [position, content]);

  const handleMouseEnter = useCallback(() => {
    if (!content || !enabled) return;
    timeoutRef.current = setTimeout(showTooltip, delay);
  }, [content, enabled, delay, showTooltip]);

  const handleMouseLeave = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsVisible(false);
  }, []);
  
  // Don't render tooltip wrapper if no content or disabled - just return children
  if (!content || !enabled) {
    return children;
  }

  return (
    <>
      <div
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={`inline-flex ${className}`}
      >
        {children}
      </div>
      
      <AnimatePresence>
        {isVisible && (
          <motion.div
            initial={{ opacity: 0, y: position === 'bottom' ? -5 : 5, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: position === 'bottom' ? -5 : 5, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="fixed z-[200] w-[280px] bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl overflow-hidden pointer-events-none"
            style={{ top: tooltipPosition.top, left: tooltipPosition.left }}
          >
            {/* Header */}
            <div className="px-3 py-2 bg-zinc-800 border-b border-zinc-700 flex items-center justify-between">
              <span className="font-medium text-white text-sm">{content.title}</span>
              {content.shortcut && (
                <kbd className="px-1.5 py-0.5 bg-zinc-700 border border-zinc-600 rounded text-[10px] font-mono text-orange-400 flex items-center gap-1">
                  <Keyboard className="w-2.5 h-2.5" />
                  {content.shortcut}
                </kbd>
              )}
            </div>
            
            {/* Body */}
            <div className="px-3 py-2 space-y-2">
              <p className="text-xs text-zinc-400 leading-relaxed">
                {content.description}
              </p>
              
              {content.tip && (
                <div className="flex items-start gap-2 pt-1 border-t border-zinc-800">
                  <Lightbulb className="w-3 h-3 text-amber-400 mt-0.5 flex-shrink-0" />
                  <p className="text-[10px] text-amber-200/80 leading-relaxed">
                    {content.tip}
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Simple wrapper that auto-detects testid
export const Tooltip = ({ children, id, ...props }) => {
  // Try to get id from child's data-testid if not provided
  const childTestId = children?.props?.['data-testid'];
  const tooltipId = id || childTestId;
  
  return (
    <ContextualTooltip id={tooltipId} {...props}>
      {children}
    </ContextualTooltip>
  );
};

// Export tooltip content for reference
export const getTooltipContent = (id) => TOOLTIP_CONTENT[id];

export default ContextualTooltip;
